import React, {
  useState,
  useCallback,
  useEffect,
  useRef,
  useMemo,
} from "react";
import { Card } from "primereact/card";
import { Button } from "primereact/button";
import { Divider } from "primereact/divider";
import {
  UserBasicInfo,
  PartnerType,
} from "@features/settings/service/userService";
import { OrganizationSettings } from "./OrganizationSettings";
import { NetworkItemCard } from "./NetworkItemCard";
import { CommonModal } from "@/core/components/commonModal/CommonModal";
import { setModalIsOpened } from "@/core/store/app";
import { SvgIcon } from "@/core/components/svgIcon/SvgIcon";
import { apiService } from "@/core/api";
import { API_ENDPOINTS } from "@/core/constants/endpoints";
import { ideasService } from "@/features/ideas/service/api";
import { AccountSetupPage } from "@/features/accountSetup/pages/AccountSetupPage";
import {
  $userBasicInfo,
  fetchUserBasicInfoRequested,
} from "@/features/user/store";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "@/core/constants/app";
import { Dialog } from "primereact/dialog";
import "./OrganizationTab.scss";
import { useToast } from "@/core/hooks/useToast";
import { useUnit } from "effector-react";

interface OrganizationSearchResult {
  id: string;
  title: string;
  created: string;
  field_created: string;
  logo_uri: string;
  partner_type: {
    id: string;
    name: string;
  };
  since: string;
  legal_form: string;
  country: {
    id: string;
    name: string;
  };
  city: string;
  pic_number: string;
  size: string;
  implemented_projects: number;
  sectors: {
    id: string;
    name: string;
  }[];
}

interface OrganizationSearchResponse {
  data: OrganizationSearchResult[];
}

interface PartnerActionPayload {
  action_type: "request" | "invitation";
  action_mode: "add" | "remove";
  target_id: string;
}

interface NetworkPartnerData {
  id: string;
  title: string;
  created: string;
  field_created: string;
  logo_uri: string;
  partner_type: {
    id: string;
    name: string;
  };
  since: string;
  legal_form: string;
  country: {
    id: string;
    name: string;
  };
  city: string;
  pic_number: string;
  size: {
    id: string;
    name: string;
  };
  implemented_projects: number;
  sectors: {
    id: string;
    name: string;
  }[];
  sponsored: boolean;
}

export interface NetworkResponse {
  status: "request" | "invitation" | "confirmed";
  action_id: string;
  partner_id: string;
  partner_data: NetworkPartnerData;
}

export const OrganizationTab: React.FC = () => {
  const basicInfo = useUnit($userBasicInfo);
  const fetchUserBasicInfo = useUnit(fetchUserBasicInfoRequested);

  useEffect(() => {
    if (!basicInfo) {
      fetchUserBasicInfo();
    }
  }, [basicInfo, fetchUserBasicInfo]);

  const isProUser = basicInfo?.pro_user ?? false;

  const isPartnerAdmin =
    basicInfo?.partner &&
    typeof basicInfo.partner === "object" &&
    basicInfo.partner.status === "admin";
  const isPartnerMember =
    basicInfo?.partner &&
    typeof basicInfo.partner === "object" &&
    basicInfo.partner.status === "member";
  const [joinIsOpen, setJoinIsOpen] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [searchResults, setSearchResults] = useState<
    OrganizationSearchResult[]
  >([]);
  const [searchLoading, setSearchLoading] = useState<boolean>(false);
  const [selectedOrg, setSelectedOrg] =
    useState<OrganizationSearchResult | null>(null);
  const [joinLoading, setJoinLoading] = useState<boolean>(false);
  const [userNetwork, setUserNetwork] = useState<NetworkResponse[]>([]);
  const [networkLoading, setNetworkLoading] = useState<boolean>(false);
  const [actionLoading, setActionLoading] = useState<{
    [key: string]: boolean;
  }>({});
  const inputRef = useRef<HTMLInputElement>(null);
  const [loading, setLoading] = useState<"accept" | "deny" | "request" | null>(
    null,
  );
  const [createOrgModalOpen, setCreateOrgModalOpen] = useState<boolean>(false);
  const toast = useToast();
  const navigate = useNavigate();

  const fetchUserNetwork = useCallback(async () => {
    setNetworkLoading(true);
    try {
      const response = await apiService.get<NetworkResponse[]>(
        API_ENDPOINTS.USER.PARTNER,
      );
      setUserNetwork(response);
      // console.log("User network data:", response);
    } catch (error) {
      console.error("Failed to fetch user network:", error);
      setUserNetwork([]);
    } finally {
      setNetworkLoading(false);
    }
  }, []);

  const handleAcceptRequest = useCallback(
    async (actionId: string, partnerId: string) => {
      setActionLoading((prev) => ({ ...prev, [actionId]: true }));
      try {
        const payload: PartnerActionPayload = {
          action_type: "invitation",
          action_mode: "add",
          target_id: partnerId,
        };

        await apiService.post(API_ENDPOINTS.PARTNERS.ACTION, payload);
        await fetchUserNetwork();
        // console.log("Request accepted successfully");
      } catch (error) {
        console.error("Failed to accept request:", error);
      } finally {
        setActionLoading((prev) => ({ ...prev, [actionId]: false }));
      }
    },
    [fetchUserNetwork],
  );

  const handleDenyRequest = useCallback(
    async (actionId: string, partnerId: string) => {
      setActionLoading((prev) => ({ ...prev, [actionId]: true }));
      try {
        const payload: PartnerActionPayload = {
          action_type: "invitation",
          action_mode: "remove",
          target_id: partnerId,
        };

        await apiService.post(API_ENDPOINTS.PARTNERS.ACTION, payload);
        await fetchUserNetwork();
        // console.log("Request denied successfully");
      } catch (error) {
        console.error("Failed to deny request:", error);
      } finally {
        setActionLoading((prev) => ({ ...prev, [actionId]: false }));
      }
    },
    [fetchUserNetwork],
  );

  useEffect(() => {
    // console.log(isPartnerAdmin, "isPartnerAdmin");
    if (!isProUser && !isPartnerAdmin) {
      fetchUserNetwork();
    }
  }, [isProUser, isPartnerAdmin, fetchUserNetwork]);

  const searchOrganizations = useCallback(async (query: string) => {
    if (query.length < 3) {
      setSearchResults([]);
      return;
    }

    setSearchLoading(true);
    try {
      const response = await apiService.get<OrganizationSearchResponse>(
        API_ENDPOINTS.PARTNERS.ORG_SEARCH(query),
      );
      setSearchResults(response.data);
    } catch (error) {
      console.error("Search failed:", error);
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      searchOrganizations(searchQuery);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchQuery, searchOrganizations]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const askToJoin = useCallback(
    async (orgId: string) => {
      setJoinLoading(true);
      try {
        const payload: PartnerActionPayload = {
          action_type: "request",
          action_mode: "add",
          target_id: orgId,
        };

        await apiService.post(API_ENDPOINTS.PARTNERS.ACTION, payload);
        await fetchUserNetwork();

        setJoinIsOpen(false);
        setModalIsOpened(false);

        // console.log("Join request sent successfully");
      } catch (error) {
        console.error("Failed to send join request:", error);
      } finally {
        setJoinLoading(false);
      }
    },
    [fetchUserNetwork],
  );

  const handleAcceptInvitation = async (action_id: string) => {
    try {
      setLoading("accept");
      await apiService.post(API_ENDPOINTS.PARTNERS.ACTION_ANSWER, {
        action_answer: "accept",
        action_id: action_id,
      });
      await fetchUserNetwork();
      fetchUserBasicInfoRequested();
    } catch (error: any) {
      console.error("Failed to accept invitation:", error?.message);
      toast.showError(error?.message || "Failed to accept invitation");
    } finally {
      setLoading(null);
    }
  };

  const handleWithdrawInvitation = async (action_id: string) => {
    try {
      setLoading("deny");
      await apiService.post(API_ENDPOINTS.PARTNERS.ACTION, {
        action_type: "request",
        action_mode: "remove",
        target_id: action_id,
      });
      await fetchUserNetwork();
      fetchUserBasicInfoRequested();
    } catch (error: any) {
      console.error("Failed to deny invitation:", error?.message);
      toast.showError(error?.message || "Failed to deny invitation");
    } finally {
      setLoading(null);
    }
  };

  const handleDenyInvitation = async (action_id: string) => {
    try {
      setLoading("deny");
      await apiService.post(API_ENDPOINTS.PARTNERS.ACTION_ANSWER, {
        action_answer: "deny",
        action_id: action_id,
      });
      await fetchUserNetwork();
      fetchUserBasicInfoRequested();
    } catch (error: any) {
      console.error("Failed to deny invitation:", error?.message);
      toast.showError(error?.message || "Failed to deny invitation");
    } finally {
      setLoading(null);
    }
  };

  const handleOrganizationCreated = () => {
    setCreateOrgModalOpen(false);
    setModalIsOpened(false);
    fetchUserBasicInfoRequested();
    navigate(ROUTES.HOME);
    toast.showSuccess("Organization created successfully!");
  };

  useEffect(() => {
    if (!joinIsOpen) {
      setSearchQuery("");
      setSearchResults([]);
      setSelectedOrg(null);
      setSearchLoading(false);
      setJoinLoading(false);
    }
  }, [joinIsOpen]);

  const modalActions = useMemo(
    () => (
      <div className="form-actions">
        <div className="form-actions-container">
          <button
            type="button"
            className="btn-secondary"
            onClick={() => {
              setJoinIsOpen(false);
              setModalIsOpened(false);
            }}
          >
            Cancel
          </button>
          <Button
            type="button"
            onClick={() => {
              if (selectedOrg) {
                askToJoin(selectedOrg.id);
              }
            }}
            disabled={!selectedOrg || joinLoading}
            loading={joinLoading}
          >
            {joinLoading ? "Sending..." : "Ask to join"}
          </Button>
        </div>
      </div>
    ),
    [selectedOrg, joinLoading, askToJoin],
  );

  const modalContent = useMemo(
    () => (
      <div className="proModal">
        <div className="proModal-title">Join an organisation</div>
        <div className="proModal-orgSearch">
          <div className="proModal-orgSearch-input">
            <div className="search-input-wrapper">
              <SvgIcon icon="icon-magnifier" className="search-icon" />
              <input
                ref={inputRef}
                type="text"
                placeholder="Search for organization"
                value={searchQuery}
                onChange={handleSearchChange}
                className="search-input"
                autoFocus
              />
            </div>
            {searchLoading && (
              <div className="search-loading">Searching...</div>
            )}
            {searchResults.length > 0 && (
              <div className="search-results">
                {searchResults.map((org) => (
                  <div
                    key={org.id}
                    className={`search-result-item ${
                      selectedOrg?.id === org.id ? "selected" : ""
                    }`}
                    onClick={() => setSelectedOrg(org)}
                  >
                    <div className="org-title">{org.title}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    ),
    [
      searchQuery,
      searchLoading,
      searchResults,
      selectedOrg,
      handleSearchChange,
    ],
  );

  if (!isPartnerAdmin && !isPartnerMember) {
    const hasNetworkData = userNetwork && userNetwork.length > 0;
    // console.log(userNetwork, "userNetwork");
    return (
      <div className="settings-organization">
        <Card className="organization-content-card">
          <div className="p-6">
            <h3 className="text-xl font-semibold m-0 text-900">Organisation</h3>

            <p className="text-600 m-0 mt-2 mb-4">
              {hasNetworkData
                ? "Your organization network and requests"
                : "Create or join an organisation to collaborate with other users"}
            </p>

            {networkLoading && (
              <div className="text-center p-4">
                <i className="pi pi-spinner pi-spin"></i>
                <p className="mt-2">Loading network data...</p>
              </div>
            )}

            {hasNetworkData && !networkLoading && (
              <div className="network-data mb-4">
                <h4 className="text-lg font-medium mb-3">Your Network</h4>
                {userNetwork.map((networkItem) => (
                  <NetworkItemCard
                    key={networkItem.action_id}
                    networkItem={networkItem}
                    loading={loading}
                    onAccept={handleAcceptInvitation}
                    onDeny={handleDenyInvitation}
                    onWithdraw={handleWithdrawInvitation}
                  />
                ))}
              </div>
            )}

            <Divider className="my-4" />

            <div className="text-right">
              <div className="inline-flex" style={{ gap: "1rem" }}>
                <Button
                  label="Join an organisation"
                  icon="pi pi-users"
                  className="p-button-outlined"
                  style={{ borderRadius: "10rem", marginRight: "1rem" }}
                  onClick={() => setJoinIsOpen(true)}
                  disabled={hasNetworkData}
                  tooltip={
                    hasNetworkData
                      ? "You already have organization connections"
                      : ""
                  }
                />
                <Button
                  label="Create your organisation"
                  icon="pi pi-plus"
                  className="p-button"
                  style={{ borderRadius: "10rem" }}
                  onClick={() => setCreateOrgModalOpen(true)}
                  disabled={hasNetworkData}
                  tooltip={
                    hasNetworkData
                      ? "You already have organization connections"
                      : ""
                  }
                />
              </div>
            </div>
          </div>
        </Card>
        <CommonModal
          onClose={() => {
            setJoinIsOpen(false);
            setModalIsOpened(false);
          }}
          hasForm={false}
          width={600}
          height={550}
          icon={""}
          isOpen={joinIsOpen}
          actions={modalActions}
        >
          {modalContent}
        </CommonModal>

        <Dialog
          visible={createOrgModalOpen}
          onHide={() => {
            setCreateOrgModalOpen(false);
            setModalIsOpened(false);
          }}
          header="Create your organisation"
          style={{ width: "1200px", height: "800px" }}
          maximizable
          modal
          draggable={false}
          className="create-org-dialog"
        >
          <AccountSetupPage
            isFromSettings={true}
            initialStep={6}
            showSkipOptions={false}
            isModal={true}
            onComplete={handleOrganizationCreated}
            showProgressBar={false}
            submitPartnerOnly={true}
          />
        </Dialog>
      </div>
    );
  }

  return <OrganizationSettings basicInfo={basicInfo} />;
};
